import { createAsyncThunk } from '@reduxjs/toolkit';
import { FirebaseError } from 'firebase/app';
import { addDoc, arrayUnion, deleteDoc, doc, getDoc, getDocs, onSnapshot, updateDoc } from 'firebase/firestore';
import { createCollection } from '../firestore-helpers';
import { Product, addOrUpdateList, removeList, type ProductList, type ProductListMap } from '../state/products';
import type { AppUser } from "../state/users";

const getListCollection = (uid: string) => {
    return createCollection<ProductList>(`users/${uid}/lists`);
}

export const getListCount = async (user: AppUser): Promise<number> => {
    const listsCollection = getListCollection(user.uid);
    const querySnapshot = await getDocs(listsCollection);
    return querySnapshot.size;
}

export const getLists = async (user: AppUser): Promise<ProductListMap> => {
    const lists = {} as ProductListMap;
    const listsCollection = getListCollection(user.uid);
    const listDocs = await getDocs(listsCollection);
    listDocs.docs.forEach((listDoc) => {
        const userList: ProductList = listDoc.data();
        console.log(`loaded list ${listsCollection.path}/${listDoc.id}: ${userList.name}`);
        lists[listDoc.id] = userList;
    });
    return lists;
}

export const saveList = async (user: AppUser, list: ProductList): Promise<void> => {
    await addDoc(getListCollection(user.uid), list);
    return;
}

export const deleteList = async (user: AppUser, listId: string): Promise<void> => {
    const count: number = await getListCount(user);
    if (count > 1) { // don't delete the last list
        const listRef = doc(getListCollection(user.uid), listId);
        await deleteDoc(listRef);
    }
    return;
}

export const addProductToList = async (uid: string, listId: string, productToAdd: Product): Promise<void> => {
    const listRef = doc(getListCollection(uid), listId);
    const listSnapshot = await getDoc(listRef);
    if (listSnapshot.exists()) {
        const listData: ProductList = listSnapshot.data();
        const existingProductIndex = listData.products.findIndex(product =>
            product.shopId === productToAdd.shopId && product.productRef === productToAdd.productRef);
        if (existingProductIndex === -1) {
            await updateDoc(listRef, {
                products: arrayUnion(productToAdd)
            });
        } else {
            console.warn(`skip adding product ${productToAdd.shopId}/${productToAdd.productRef}, already exists in list ${listRef.path}`);
        }
    } else {
        throw new Error(`list not found: ${listRef.path} does not exist`);
    }
}

export const removeProductFromList = async (uid: string, listId: string, productToRemove: Product): Promise<void> => {
    const listRef = doc(getListCollection(uid), listId);
    const listSnapshot = await getDoc(listRef);
    if (listSnapshot.exists()) {
        const listData: ProductList = listSnapshot.data();
        const products: Product[] = listData.products || [];
        const filteredProducts = products.filter(product =>
            product.shopId !== productToRemove.shopId ||
            product.productRef !== productToRemove.productRef
        );
        await updateDoc(listRef, { products: filteredProducts });
    } else {
        throw new Error(`list not found: ${listRef.path} does not exist`);
    }
}


let unsubscribeUserLists = (): void => { };

export const startListeningUserLists = createAsyncThunk<
    void,
    AppUser,
    {
        rejectValue: FirebaseError;
    }
>(
    'products/startListeningUserLists',
    async (user: AppUser, thunkAPI) => {
        unsubscribeUserLists();
        const collectionRef = getListCollection(user.uid);
        // Listen for changes on the collection
        onSnapshot(collectionRef, snapshot => {
            snapshot.docChanges().forEach(change => {
                if (change.type === 'added') {
                    thunkAPI.dispatch(addOrUpdateList({ id: change.doc.id, list: change.doc.data() }));
                }
                if (change.type === 'modified') {
                    thunkAPI.dispatch(addOrUpdateList({ id: change.doc.id, list: change.doc.data() }));
                }
                if (change.type === 'removed') {
                    thunkAPI.dispatch(removeList({ id: change.doc.id }));
                }
            });
        }, (error: FirebaseError) => {
            return thunkAPI.rejectWithValue(error);
        });
    }
);
export const stopListeningUserLists = createAsyncThunk<
    void,
    void,
    {}
>(
    'products/stopListeningUserLists',
    () => {
        unsubscribeUserLists();
    }
);
